// src/pages/AgentManagement/index.tsx
// Main page component for Agent Management

import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, CardBody, CardHeader, Button, Input } from 'reactstrap';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { useLocation } from 'react-router-dom';

// Absolute imports (no ../../)
import BreadCrumb from 'Components/Common/BreadCrumb';
import AgentList from './AgentList';
import AgentPagination from './AgentPagination';
import CreateAgentModal, { DEFAULT_AGENT_PASSWORD } from './modals/CreateAgentModal';
import EditAgentModal from './modals/EditAgentModal';
import { PageActionPermission } from 'services/pageActionPermission/PageActionPermission';
import { getRoles, getTenants, getUserProfile } from 'helpers/fakebackend_helper';
import { Department, Role } from 'pages/UserManagement/types';

// Redux
import { fetchAgents, createAgent, updateAgent, changeAgentStatus } from 'slices/agentManagement/thunk';
import {
  openModal,
  closeModal,
  setSelectedAgent,
  setPage,
  setFilters,
} from 'slices/agentManagement/reducer';

// Types
import { IAgent } from 'slices/agentManagement/types';

// Hooks
import { useBrandingTitle } from '../../hooks';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('agent_management');

const AgentManagement: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  useBrandingTitle(st('quan_ly_dai_ly_text'));

  // Redux state
  const {
    agents,
    selectedAgent,
    pagination,
    filters,
    loading,
    modalState,
  } = useSelector((state: any) => state.AgentManagement);
  
  // Load agents on mount and when filters/pagination change
  useEffect(() => {
    const params = {
      PageIndex: pagination.currentPage,
      PageSize: pagination.pageSize,
      ...filters,
    };
    dispatch(fetchAgents(params) as any);
  }, [dispatch, pagination.currentPage, pagination.pageSize, filters]);

  // Danh sách Role — dùng cho dropdown chọn vai trò tài khoản khi tạo đại lý (mục 16,
  // REQUIRE_FIXBUGS.md), cùng cách UserManagement đang tải.
  const [roles, setRoles] = useState<Role[]>([]);
  useEffect(() => {
    const loadRoles = async () => {
      try {
        const response = await getRoles({ pageSize: 1000 });
        const data = response.data || response;
        const roleList = Array.isArray(data) ? data : (data.items || data.data || []);
        setRoles(roleList);
      } catch (error) {
        setRoles([]);
      }
    };
    loadRoles();
  }, []);

  // Danh sách Phòng ban (= Tenant, cùng cách UserManagement đang tải) — dùng cho dropdown "Phòng
  // ban" khi tạo/sửa đại lý (mục 19, REQUIRE_FIXBUGS.md). defaultDepartmentId lấy từ profile người
  // đang đăng nhập để prefill sẵn, nhưng dropdown vẫn cho chọn phòng ban khác.
  const [departments, setDepartments] = useState<Department[]>([]);
  const [defaultDepartmentId, setDefaultDepartmentId] = useState<number | null>(null);
  useEffect(() => {
    const loadDepartments = async () => {
      try {
        const response = await getTenants({ pageSize: 1000 });
        const data = response.data || response;
        const departmentList = Array.isArray(data) ? data : (data.items || data.data || []);
        setDepartments(departmentList);
      } catch (error) {
        setDepartments([]);
      }
    };
    const loadCurrentTenant = async () => {
      try {
        const response = await getUserProfile();
        const data = response.data || response;
        if (data?.tenantId) setDefaultDepartmentId(Number(data.tenantId));
      } catch (error) {
        setDefaultDepartmentId(null);
      }
    };
    loadDepartments();
    loadCurrentTenant();
  }, []);

  // Handlers
  const handleCreateClick = () => {
    dispatch(openModal('create'));
  };
  
  const handleEditClick = (agent: IAgent) => {
    dispatch(setSelectedAgent(agent));
    dispatch(openModal('edit'));
  };
  
  const handleCreateSubmit = async (values: any) => {
    // Convert % to decimal for API (15% → 0.15); departmentId Formik giữ dạng string (mục 19,
    // REQUIRE_FIXBUGS.md — bỏ trống thì backend tự fallback về tenant của người đang tạo)
    const payload = {
      ...values,
      commissionRate: Number(values.commissionRate) / 100,
      // Mục 5 (README.md §7) — % → decimal cho API (5% → 0.05)
      prizePayoutCommissionRate: Number(values.prizePayoutCommissionRate) / 100,
      departmentId: values.departmentId ? Number(values.departmentId) : undefined,
    };

    const result = await dispatch(createAgent(payload) as any);

    // Toast handling in component (not in thunk)
    if (createAgent.fulfilled.match(result)) {
      toast.success(
        st('luu_thong_tin_dai_ly_thanh_message', { value: String(values.code || '').toLowerCase() }) +
          st('mat_khau_mac_dinh_default_agent_message', { default_agent_password: DEFAULT_AGENT_PASSWORD }),
        { autoClose: 10000 },
      );
      dispatch(closeModal('create'));
      // Reload list
      dispatch(fetchAgents({
        PageIndex: pagination.currentPage,
        PageSize: pagination.pageSize,
        ...filters,
      }) as any);
    } else if (createAgent.rejected.match(result)) {
      // toast.error(result.payload?.message || 'Có lỗi xảy ra');
    }
  };
  
  const handleUpdateSubmit = async (values: any) => {
    // Convert % to decimal for API; roleId/departmentId Formik giữ dạng string, backend cần
    // number|null (mục 18/19, REQUIRE_FIXBUGS.md — chỉ gửi khi đại lý đã có tài khoản)
    const payload = {
      ...values,
      id: selectedAgent?.id,
      commissionRate: Number(values.commissionRate) / 100,
      prizePayoutCommissionRate: Number(values.prizePayoutCommissionRate) / 100,
      roleId: values.roleId ? Number(values.roleId) : undefined,
      email: values.email || undefined,
      departmentId: values.departmentId ? Number(values.departmentId) : undefined,
    };

    const result = await dispatch(updateAgent(payload) as any);

    // Toast handling in component
    if (updateAgent.fulfilled.match(result)) {
      // Trạng thái đại lý cập nhật qua endpoint riêng (PUT /Agent/{id}/status) — chỉ gọi khi có
      // thay đổi so với trạng thái hiện tại (mục 8.8, READE.md)
      if (selectedAgent?.id && values.status && values.status !== selectedAgent.status) {
        await dispatch(changeAgentStatus({ id: selectedAgent.id, status: values.status }) as any);
      }
      toast.success(st('luu_thong_tin_dai_ly_thanh_message_2'));
      dispatch(closeModal('edit'));
      dispatch(setSelectedAgent(null));
      // Reload list
      dispatch(fetchAgents({
        PageIndex: pagination.currentPage,
        PageSize: pagination.pageSize,
        ...filters,
      }) as any);
    } else if (updateAgent.rejected.match(result)) {
      // toast.error(result.payload?.message || 'Có lỗi xảy ra');
    }
  };

  const handlePageChange = (page: number) => {
    dispatch(setPage(page));
  };

  const handleStatusFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setFilters({ Status: e.target.value as any }));
  };
  
  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
                    {/* Breadcrumb */}
          <BreadCrumb title={st('quan_ly_dai_ly_text')} pageTitle={st('quan_ly_text')} />
          
          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader className="d-flex align-items-center justify-content-between">
                                    <div>
                    <h5 className="card-title mb-0">{st('quan_ly_dai_ly_text')}</h5>
                    {/* <p className="text-muted mb-0">
                      Quản lý thông tin đại lý trong hệ thống
                    </p> */}
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <Input
                      type="select"
                      style={{ width: 200 }}
                      value={filters.Status || ''}
                      onChange={handleStatusFilterChange}
                    >
                      <option value="">{st('tat_ca_trang_thai_option')}</option>
                      <option value="Active">{st('dang_hoat_dong_label')}</option>
                      <option value="Inactive">{st('ngung_hoat_dong_label')}</option>
                      <option value="Locked">{st('tam_khoa_label')}</option>
                    </Input>
                    {PageActionPermission.canDisplay(location.pathname, "button-add") && (
                    <Button
                      color="success"
                      data-action-code="button-add"
                      data-action-name="Thêm đại lý"
                      onClick={handleCreateClick}
                    >
                      <i className="ri-add-line me-1"></i>
                      {st('them_dai_ly_button')}
                    </Button>
                    )}
                  </div>
                </CardHeader>
                
                <CardBody>
                  {/* Agent List Table */}
                  <AgentList
                    agents={agents}
                    loading={loading}
                    onEdit={handleEditClick}
                    page={pagination.currentPage}
                    pageSize={pagination.pageSize}
                  />
                  
                  {/* Pagination */}
                  <AgentPagination
                    currentPage={pagination.currentPage}
                    totalPages={pagination.totalPages}
                    totalItems={pagination.totalItems}
                    pageSize={pagination.pageSize}
                    onPageChange={handlePageChange}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      
      {/* Modals */}
      <CreateAgentModal
        isOpen={modalState.create}
        toggle={() => dispatch(closeModal('create'))}
        roles={roles}
        departments={departments}
        defaultDepartmentId={defaultDepartmentId}
        onSubmit={handleCreateSubmit}
      />

      <EditAgentModal
        isOpen={modalState.edit}
        toggle={() => dispatch(closeModal('edit'))}
        agent={selectedAgent}
        roles={roles}
        departments={departments}
        onSubmit={handleUpdateSubmit}
      />
    </React.Fragment>
  );
};

export default AgentManagement;